/**
 * Which portal build a signed-in user lands on.
 *
 * The two portal builds are served by the edge, which picks between them on
 * the `ui_theme` cookie alone — `modern` selects the new portal, anything
 * else the classic one. A user's stored theme preference has no effect until
 * that cookie agrees with it, so an operator setting the preference for
 * someone else changes nothing on that person's next visit. Reconciling the
 * cookie against the stored preference at sign-in makes the stored value the
 * one that decides.
 *
 * Contains:
 * - THEME_PREFERENCE_KEY — where the stored preference lives
 * - THEME_COOKIE / THEME_MODERN / THEME_CLASSIC — the edge's vocabulary
 * - readThemeCookie / writeThemeCookie — the browser's current answer
 * - resolveThemeCookieAction — pure stored-vs-cookie decision
 * - applyThemeRouting — writes the cookie and reports whether to reload
 *
 * Returns plain JS — no JSX, no redux.
 */

export const THEME_PREFERENCE_KEY = 'app.customization.theme';
export const THEME_COOKIE = 'ui_theme';
export const THEME_MODERN = 'modern';
export const THEME_CLASSIC = 'classic';

// Feature key (auth payload `features`) that exposes the classic/modern
// switch. It gates only the toggle's visibility — never which build a user
// is on, so revoking it strands nobody. Staff carry it implicitly.
export const MODERN_UI_FEATURE = 'MODERN_UI';

// The cookie outlives a session so a returning visitor keeps their build
// without waiting on a preference fetch.
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Read the build-selection cookie.
 *
 * @returns {string|null} The cookie's value, or null when it isn't set.
 */
export const readThemeCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)ui_theme=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Write the build-selection cookie.
 *
 * @param {string} theme - `modern` or `classic`.
 * @returns {void}
 */
export const writeThemeCookie = theme => {
  document.cookie = `${THEME_COOKIE}=${theme};path=/;max-age=${COOKIE_MAX_AGE_SECONDS};SameSite=Lax;Secure`;
};

/**
 * Decide what the build-selection cookie should become.
 *
 * @param {string|null|undefined} storedTheme - The user's stored theme
 *   preference, as `app.customization.theme` holds it.
 * @param {string|null|undefined} cookieTheme - The cookie's current value.
 * @returns {string|null} The value to write, or null to leave the cookie
 *   alone. An unknown or absent stored preference leaves it alone, so a
 *   failed preference fetch never bounces someone between builds.
 */
export const resolveThemeCookieAction = (storedTheme, cookieTheme) => {
  if (storedTheme !== THEME_MODERN && storedTheme !== THEME_CLASSIC) return null;
  // The edge treats every non-`modern` value as classic, so a cookie of
  // `classic`, something stale, or nothing at all all mean the same build.
  const servedTheme = cookieTheme === THEME_MODERN ? THEME_MODERN : THEME_CLASSIC;
  return storedTheme === servedTheme ? null : storedTheme;
};

/**
 * Bring the build-selection cookie in line with the stored preference.
 *
 * @param {string|null|undefined} storedTheme - The stored theme preference. An
 *   absent or unrecognized value leaves the visitor on whichever build they
 *   already have, so someone who never chose a theme is never moved.
 * @returns {boolean} True when the cookie changed and the page must reload to
 *   pick up the other build; false when it already agreed or could not be
 *   written. Confirming the write matters: a caller that reloads on an
 *   unwritable cookie would reload forever.
 */
export const applyThemeRouting = storedTheme => {
  const next = resolveThemeCookieAction(storedTheme, readThemeCookie());
  if (next === null) return false;
  writeThemeCookie(next);
  return readThemeCookie() === next;
};
